import type { LegacyProviderConfigDto } from "@docket/consumer-app-client-typescript-axios";
import { DateTime } from "luxon";
import { t } from "../i18n";
import type { ImmunizationRecord, ImmunizationSeries } from "../models/Interfaces";
import type { IzProvider } from "../models/providers/IzProviders";
import { getDateTime } from "../utils/data";

// The records' cards, as the React app's PatientCard and RecordCard drew them.

const IMAGES = "/assets/images";

/** A registry's configuration: which features it has. */
export function providerConfig(
  configs: readonly LegacyProviderConfigDto[] | undefined,
  providerKey: string,
): LegacyProviderConfigDto | undefined {
  const key = providerKey.toLowerCase();
  return configs?.find((config) => config.iz_provider.destination_id.toLowerCase() === key);
}

/** A series' status, as the cards compare it. */
export const statusOf = (series: ImmunizationSeries) => series.status.toLowerCase();

// Checked in order: the first rule that matches picks the picture.
const is =
  (...names: string[]) =>
  (virus: string) =>
    names.includes(virus);
const has =
  (...parts: string[]) =>
  (virus: string) =>
    parts.some((part) => virus.includes(part));

const VIRUS_IMAGES: [matches: (virus: string) => boolean, image: string][] = [
  [is("hep-a", "hep a", "hepa", "hepatitis a"), "HepatitisA.png"],
  [is("hep-b", "hep b", "hepb", "hepatitis b"), "HepatitisB.png"],
  [has("easles", "mumps", "rubella", "mmr"), "MMR.png"],
  [has("covid"), "Covid19.png"],
  [is("hib", "haemophilus influenzae b"), "HIB.png"],
  [has("monkey", "small"), "monkeypox.png"],
  [has("pox", "var"), "Varicella.png"],
  [has("flu"), "Influenza.png"],
  [
    (virus) => has("td", "dtp", "tap")(virus) || virus === "diphtheria, tetanus, pertussis",
    "DTap.png",
  ],
  [has("gococcal", "mening"), "Meningococcal.png"],
  [has("neumo"), "Pneumococcal.png"],
  [has("roto", "rota"), "Rotavirus.png"],
  [has("polio"), "Polio.png"],
  [has("-19"), "Covid19.png"],
  [has("hpv"), "HumanPapilloma.png"],
  [has("zoster"), "zoster.png"],
  [has("typhoid"), "typhoid.png"],
  [has("rabies"), "rabies.png"],
  [has("encephalitis"), "Japanese_Encephalitis.png"],
  [has("yellow"), "yellow_fever.png"],
  [has("tuberculosis", "bsv"), "Tuberculosis.png"],
  [has("rsv", "respiratory syncytial virus"), "RSV.png"],
  [has("cholera"), "Cholera.png"],
  [has("adenovirus"), "Adenovirus.png"],
  [has("anthrax"), "Anthrax.png"],
];

/** The picture on a series' card, for its vaccine. */
export function virusImage(vaccineType: string): string {
  const virus = vaccineType.toLowerCase();
  const image = VIRUS_IMAGES.find(([matches]) => matches(virus))?.[1] ?? "Vial_Default.png";
  return `${IMAGES}/${image}`;
}

/** The QR code button, in the status' color. */
export function qrImage(status: string, izProvider: IzProvider, dark: boolean): string {
  let color = "Blue";
  if (status === "overdue" || (status === "due_now" && izProvider.statusImages.redOnDue)) {
    color = "Red";
  } else if (
    status === "due_now" ||
    (status === "current" && izProvider.statusImages.yellowOnCurrent)
  ) {
    color = "Yeller";
  } else if (status === "current" || status === "complete") {
    color = "Green";
  }
  return `${IMAGES}/Icon_QR_Code_${color}${dark ? "_HC" : ""}.png`;
}

/** The add-to-calendar button, in the status' color; none for the other statuses. */
export function calendarImage(status: string, izProvider: IzProvider, dark: boolean): string {
  let name = "";
  if (status === "overdue" || (status === "due_now" && izProvider.statusImages.redOnDue)) {
    name = "DUE";
  } else if (
    status === "due_now" ||
    (status === "current" && izProvider.statusImages.yellowOnCurrent)
  ) {
    name = "DUE-NOW";
  } else if (status === "current") {
    name = "CURRENT";
  }
  return name ? `${IMAGES}/Icon_Calendar_${name}${dark ? "_Dark" : ""}.png` : "";
}

/** The vaccine, shortened for the card; "Records Processing" until the registry names it. */
export function seriesTitle(vaccineType: string, shorten = true): string {
  if (!vaccineType || vaccineType === "wait") {
    return t("immunizations.records_processing");
  }
  return shorten && vaccineType.length > 23 ? `${vaccineType.substring(0, 20)}...` : vaccineType;
}

/** When the next dose is due or can be given. */
const nextDate = (series: ImmunizationSeries) => series.dateDue || series.earliestDateToGive;

const DATED = new Set(["overdue", "due_now", "due", "current"]);

/** The card's due date: none unless the series is due or current. */
export function dueDate(series: ImmunizationSeries): string {
  const date = nextDate(series);
  return date && DATED.has(statusOf(series))
    ? getDateTime(date).toLocaleString()
    : t("immunizations.records_no_due_date");
}

/** When the next dose is due, in the series' sheet. */
export function dueWindow(series: ImmunizationSeries): { label: string; value: string } {
  const date = (value: string) => getDateTime(value).toLocaleString();
  if (series.dateDue && series.dateOverdue) {
    return {
      label: t("immunizations.records_due_on"),
      value: `${date(series.dateDue)} - ${date(series.dateOverdue)}`,
    };
  }
  if (series.dateOverdue) {
    return { label: t("immunizations.records_overdue_after"), value: date(series.dateOverdue) };
  }
  if (series.dateDue) {
    return { label: t("immunizations.records_due_on_or_after"), value: date(series.dateDue) };
  }
  return {
    label: t("immunizations.records_due_on"),
    value: t("immunizations.records_no_due_date"),
  };
}

/** Whether the series has a SMART Health Card: a COVID-19 series with shots, where it's offered. */
export function hasHealthCard(
  series: ImmunizationSeries,
  config: LegacyProviderConfigDto | undefined,
): boolean {
  return (
    series.vaccineType.toLowerCase().includes("covid") &&
    series.events.length > 0 &&
    !!config?.features?.qr_code
  );
}

/**
 * "1 Shot in NJIIS" (doses in Alaska). Beside a QR code it was " In NJIIS", untranslated, to leave
 * the code room in the apps.
 */
export function shotsLabel(
  record: Pick<ImmunizationRecord, "izProviderKey">,
  series: ImmunizationSeries,
  izProvider: IzProvider,
  healthCard: boolean,
): string {
  const count = series.events.length;
  const shots =
    record.izProviderKey.toLowerCase() === "ak"
      ? t("immunizations.records_doses", { count })
      : t("immunizations.records_shots", { count });
  const recordedIn = healthCard
    ? ` In ${izProvider.iisAbbrev}`
    : t("immunizations.recorded_in", { iisAbbrev: izProvider.iisAbbrev });
  return `${shots} ${recordedIn}`;
}

/** Whether the next dose is due after `now`: then it can go in the calendar. */
export function hasFutureDate(series: ImmunizationSeries, now: DateTime = DateTime.now()): boolean {
  const date = nextDate(series);
  return !!date && getDateTime(date) > now;
}

const REMINDER =
  "Docket® Immunization Reminder: Please check your Docket® account for more information about your recommended immunization(s). Be sure to consult a healthcare provider before getting a new vaccine.";

/** The calendar file for the next dose: an all-day event with a reminder, as before. */
export function calendarEvent(series: ImmunizationSeries, now: DateTime, timeZone: string): string {
  const date = getDateTime(nextDate(series) ?? "");
  const stamp = now.toFormat("yyyyLLdd'T'HHmmss");
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "PRODID:-//Docket//Docket Immunization Reminder//EN",
    `X-WR-TIMEZONE:${timeZone}`,
    "BEGIN:VEVENT",
    `DTSTART;VALUE=DATE:${date.toFormat("yyyyLLdd")}`,
    `DTEND;VALUE=DATE:${date.plus({ days: 1 }).toFormat("yyyyLLdd")}`,
    `DTSTAMP:${stamp}`,
    `UID:${date.toFormat("yyyyLLdd'T'HHmmss")}-${stamp}`,
    `CREATED:${stamp}`,
    `DESCRIPTION:${REMINDER}`,
    "SUMMARY:Immunization Reminder",
    "TRANSP:TRANSPARENT",
    "BEGIN:VALARM",
    "ACTION:DISPLAY",
    "DESCRIPTION:Reminder",
    "TRIGGER;RELATED=START:-PT0M",
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

/**
 * The page that hands the next dose's event to the calendar (/add-event, with the event as a data
 * URL), from `origin` (the web app's, in the apps).
 */
export function addEventURL(
  series: ImmunizationSeries,
  origin = "",
  now: DateTime = DateTime.now(),
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
): string {
  const link = `data:text/calendar,${encodeURIComponent(calendarEvent(series, now, timeZone))}`;
  return `${origin}/add-event?link=${link}`;
}
